import { Button, Col, Form, Input, Row } from 'antd';
import cls from 'clsx';
import { useEffect, useState } from 'react';
import type { ChangeEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import type { ApiError } from '@/shared/api/types';
import { images } from '@/shared/assets/images/images';
import { validateEmail } from '@/shared/lib/email';
import {
  NAME_RULE_MESSAGE,
  PERSON_NAME_PATTERN,
} from '@/shared/lib/personName';
import { Fade } from '@/shared/ui/Fade';

import s from '@/shared/ui/AuthForm.module.css';

// The terms of service document (AU-2-04); set per environment
const TERMS_OF_SERVICE_URL = import.meta.env.VITE_TERMS_OF_SERVICE_URL;

interface SignUpFormValues {
  first_name: string;
  last_name: string;
  email: string;
  accept?: boolean;
  confirm?: string;
}

interface SignUpEmailFormProps {
  onSubmit: (values: SignUpFormValues) => void;
  // The failed request, as `parseApiError` returns it
  error?: ApiError | null;
  children?: ReactNode;
}

export function SignUpEmailForm({ error, ...props }: SignUpEmailFormProps) {
  const [errorText, setErrorText] = useState<ReactNode>(null);

  const [isVisibleEmail, setIsVisibleEmail] = useState(false);
  const [isVisibleBtn, setIsVisibleBtn] = useState(false);
  const [isBackError, setIsBackError] = useState(false);

  const [form] = Form.useForm<SignUpFormValues>();
  const { t } = useTranslation();

  useEffect(() => {
    if (error) {
      setErrorText(error.message);
      setIsBackError(true);
    }
  }, [error]);

  const onFinish = (values: SignUpFormValues) => {
    const data = { ...values };
    delete data.accept;
    delete data.confirm;
    props.onSubmit(values);
  };

  const onFinishFailed = ({
    errorFields,
  }: {
    errorFields?: { errors: string[] }[];
  }) => {
    if (errorFields) setErrorText(errorFields[0]?.errors);
  };

  function handleClearError(_e: unknown, field: keyof SignUpFormValues) {
    form.setFields([
      {
        name: field,
        errors: [],
      },
    ]);
    let newError: string | null = null;
    const errorList = form.getFieldsError();

    errorList.forEach((field) => {
      if (field.errors.length && !newError) newError = field.errors[0] ?? null;
    });
    setIsBackError(false);
    setErrorText(newError);
  }

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target;

    if (name === 'last_name') {
      if (value) {
        if (!isVisibleEmail) setIsVisibleEmail(true);
        if (!isVisibleBtn && validateEmail(form.getFieldValue('email')))
          setIsVisibleBtn(true);
      } else {
        if (isVisibleEmail) setIsVisibleEmail(false);
        if (isVisibleBtn) setIsVisibleBtn(false);
      }
    }

    if (name === 'email') {
      if (validateEmail(value)) {
        if (!isVisibleBtn) setIsVisibleBtn(true);
      } else {
        if (isVisibleBtn) setIsVisibleBtn(false);
      }
    }
  }

  return (
    <Form
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      validateTrigger="onSubmit"
      className={s.form}
      layout="vertical"
    >
      <Form.Item noStyle shouldUpdate={true}>
        {() => (
          <div className={cls(s.errorText, !errorText && s.errorTextEmpty)}>
            {isBackError ? (
              <>
                {errorText + ` ${t('Kindly try another one or')} `}{' '}
                <Link to="/login">{t('Sign In')}</Link>
              </>
            ) : (
              errorText
            )}
          </div>
        )}
      </Form.Item>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label=""
            name="first_name"
            rules={[
              { required: true, message: t('Kindly insert your first name') },
              {
                type: 'string',
                message: t(NAME_RULE_MESSAGE),
                pattern: PERSON_NAME_PATTERN,
                transform(value) {
                  if (value) return value.trim();
                },
              },
            ]}
          >
            <Input
              placeholder={t('First Name')}
              onChange={(e) => handleClearError(e, 'first_name')}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label=""
            name="last_name"
            rules={[
              { required: true, message: t('Kindly insert your last name') },
              {
                type: 'string',
                message: t(NAME_RULE_MESSAGE),
                pattern: PERSON_NAME_PATTERN,
                transform(value) {
                  if (value) return value.trim();
                },
              },
            ]}
          >
            <Input
              placeholder={t('Last Name')}
              name="last_name"
              onChange={(e) => {
                handleChange(e);
                handleClearError(e, 'last_name');
              }}
            />
          </Form.Item>
        </Col>
      </Row>

      <Fade inProp={isVisibleEmail}>
        <Form.Item
          label=""
          name="email"
          validateStatus={isBackError ? 'error' : ''}
          rules={[
            { required: true, message: t('Please insert your Email') },
            { type: 'email', message: t('The email you entered is incorrect') },
          ]}
        >
          <Input
            placeholder={t('Email Address')}
            name="email"
            type="email"
            onChange={(e) => {
              handleChange(e);
              handleClearError(e, 'email');
            }}
            prefix={<img src={images.email} alt="" />}
          />
        </Form.Item>
      </Fade>

      <Fade inProp={isVisibleBtn} className={s.pushDown}>
        <Button
          type="primary"
          htmlType="submit"
          className={s.submit}
          disabled={!isVisibleBtn}
        >
          {t('Create Account')}
        </Button>
      </Fade>

      {TERMS_OF_SERVICE_URL ? (
        <div
          className={cls(s.footerLink, s.footerLinkTight)}
          hidden={!isVisibleBtn}
        >
          {t('By signing up, you agree to our')}{' '}
          <a
            href={TERMS_OF_SERVICE_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('Terms of Service')}
          </a>
        </div>
      ) : null}

      {props.children}
    </Form>
  );
}
